import { CallLimitSegment, CallLimits } from '../models/interfaces/call';

export type SlaTone = 'ok' | 'warn' | 'late';
export type SlaStage = 'pickup' | 'delivery' | 'waiting';

export const WAITING_SLA_MINUTES = 5;
const WAITING_SLA_MS = WAITING_SLA_MINUTES * 60 * 1000;

export interface SlaHistoryItem {
  status: string;
  date: Date;
}

export interface SlaCallLike {
  status: string;
  createdAt?: Date | { toDate?: () => Date } | string;
  clientAddress?: string;
  destinations?: {
    address?: string;
    duration?: number | null;
    durationInfo?: string;
  }[];
  statusHistory?: SlaHistoryItem[];
  limits?: CallLimits | null;
  driver?: {
    id?: string | null;
    name?: string | null;
    photo?: string | null;
  };
  drivers?: (string | { id?: string })[] | null;
}

export interface SlaView {
  stage: SlaStage;
  stageLabel: string;
  destinationLabel: string;
  remainingMs: number;
  limitMs: number;
  tone: SlaTone;
  formatted: string;
  expectedAt: Date;
  expectedClock: string;
  tightDistance: boolean;
}

const WARN_MS = 2 * 60 * 1000;

const toDate = (value?: Date | { toDate?: () => Date } | string | null): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'object' && typeof (value as { toDate?: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  const parsed = new Date(value as string);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatCountdown = (ms: number): string => {
  const abs = Math.abs(ms);
  const totalSec = Math.floor(abs / 1000);
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  const pad = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  return ms < 0 ? `-${pad}` : pad;
};

export const formatSlaClock = (remainingMs: number, limitMs: number): string => (
  `${formatCountdown(remainingMs)}/${formatCountdown(limitMs)}`
);

export const formatExpectedClock = (date: Date): string => {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

export const slaTone = (remainingMs: number): SlaTone => {
  if (remainingMs <= 0) return 'late';
  if (remainingMs <= WARN_MS) return 'warn';
  return 'ok';
};

export const slaToneBadge = (tone: SlaTone): string => {
  if (tone === 'late') return 'danger';
  if (tone === 'warn') return 'warning';
  return 'success';
};

const lastHistoryDate = (history: SlaHistoryItem[] | undefined, status: string): Date | null => {
  const matches = (history || [])
    .filter((item) => item.status === status)
    .map((item) => toDate(item.date))
    .filter((date): date is Date => date !== null);
  if (!matches.length) return null;
  return matches.reduce((latest, date) => (date > latest ? date : latest));
};

const lastDateOnOrBefore = (
  history: SlaHistoryItem[] | undefined,
  status: string,
  before: Date | null
): Date | null => {
  const matches = (history || [])
    .filter((item) => item.status === status)
    .map((item) => toDate(item.date))
    .filter((date): date is Date => date !== null)
    .filter((date) => !before || date.getTime() <= before.getTime());
  if (!matches.length) return null;
  return matches.reduce((latest, date) => (date > latest ? date : latest));
};

/** Coleta: se houve encaminhamento, o relógio começa nele; o aceite confirma a etapa. */
const pickupSlaAnchor = (history: SlaHistoryItem[] | undefined): Date | null => {
  const acceptedAt = lastHistoryDate(history, 'accepted');
  const forwardedAt = lastDateOnOrBefore(history, 'forwarded', acceptedAt);
  if (forwardedAt) return forwardedAt;
  return acceptedAt;
};

const waitingSlaAnchor = (call: SlaCallLike): Date | null => {
  const forwardedAt = lastHistoryDate(call.statusHistory, 'forwarded');
  if (forwardedAt) return forwardedAt;
  const waitingAt = lastHistoryDate(call.statusHistory, 'waiting');
  if (waitingAt) return waitingAt;
  return toDate(call.createdAt);
};

export const isForwardedWaiting = (call: SlaCallLike): boolean => {
  if (call.status === 'forwarded') return true;
  if (call.driver?.photo === 'forwarded') return true;
  const forwardedAt = lastHistoryDate(call.statusHistory, 'forwarded');
  if (!forwardedAt) return false;
  const acceptedAt = lastHistoryDate(call.statusHistory, 'accepted');
  return !acceptedAt || forwardedAt > acceptedAt;
};

const driverIdsFromList = (drivers: SlaCallLike['drivers']): string[] => {
  const ids = (drivers || [])
    .map((item) => (typeof item === 'string' ? item : item?.id))
    .filter((id): id is string => Boolean(id));
  return Array.from(new Set(ids));
};

/** Quem recebe o alerta no app: condutor atribuído, ou IDs de `drivers` quando a chamada está encaminhada. */
export const getSlaAlertDrivers = (call: SlaCallLike): { id: string; name: string }[] => {
  const assignedId = call.driver?.id;
  if (assignedId && call.driver?.photo !== 'forwarded') {
    return [{ id: assignedId, name: call.driver?.name || 'Condutor' }];
  }
  if (!isForwardedWaiting(call)) return [];
  return driverIdsFromList(call.drivers).map((id) => ({ id, name: 'Condutor' }));
};

const buildSlaView = (
  stage: SlaStage,
  stageLabel: string,
  destinationLabel: string,
  remainingMs: number,
  limitMs: number,
  expectedAt: Date,
  tightDistance: boolean
): SlaView => ({
  stage,
  stageLabel,
  destinationLabel,
  remainingMs,
  limitMs,
  tone: slaTone(remainingMs),
  formatted: formatSlaClock(remainingMs, limitMs),
  expectedAt,
  expectedClock: formatExpectedClock(expectedAt),
  tightDistance
});

const segmentMs = (segment?: CallLimitSegment | null): number | null => {
  if (segment == null || segment.durationInMinutes == null) return null;
  return segment.durationInMinutes * 60 * 1000;
};

const destinationLegMs = (
  dest?: { duration?: number | null; durationInfo?: string },
  segment?: CallLimitSegment | null
): number => {
  if (dest?.duration != null && !Number.isNaN(Number(dest.duration))) {
    return Number(dest.duration) * 1000;
  }
  if (segment?.duration != null && !Number.isNaN(Number(segment.duration))) {
    return Number(segment.duration) * 1000;
  }
  if (segment?.durationInMinutes != null) {
    return segment.durationInMinutes * 60 * 1000;
  }
  const match = dest?.durationInfo?.match(/(\d+)/);
  if (match) return Number(match[1]) * 60 * 1000;
  return 0;
};

/** Soma a duração dos destinos anteriores ao destino atual. */
const previousDestinationsMs = (call: SlaCallLike, currentIndex: number): number => {
  if (currentIndex <= 0) return 0;
  let total = 0;
  for (let i = 0; i < currentIndex; i += 1) {
    total += destinationLegMs(call.destinations?.[i], call.limits?.delivery?.[i]);
  }
  return total;
};

const isTightDistance = (segment: CallLimitSegment | null | undefined, remainingMs: number): boolean => {
  if (segment?.distance == null || remainingMs <= 0) return false;
  const meters = segment.distance > 100 ? segment.distance : segment.distance * 1000;
  const km = meters / 1000;
  const remainingMin = remainingMs / 60000;
  return remainingMin < km * 2;
};

export const activeDeliveryIndex = (call: SlaCallLike): number => {
  const deliveryCount = call.limits?.delivery?.length ?? call.destinations?.length ?? 1;
  const partials = (call.statusHistory || []).filter((item) => item.status === 'partialComplete').length;
  return Math.min(partials, Math.max(deliveryCount - 1, 0));
};

export const getActiveSla = (call: SlaCallLike, now: number): SlaView | null => {
  if (call.status === 'waiting' || call.status === 'forwarded') {
    const anchor = waitingSlaAnchor(call);
    if (!anchor) return null;
    const remainingMs = WAITING_SLA_MS - (now - anchor.getTime());
    const expectedAt = new Date(anchor.getTime() + WAITING_SLA_MS);
    return buildSlaView(
      'waiting',
      isForwardedWaiting(call) ? 'Aceite' : 'Aguardando',
      isForwardedWaiting(call) ? 'Encaminhada — aguardando aceite' : 'Sem condutor atribuído',
      remainingMs,
      WAITING_SLA_MS,
      expectedAt,
      false
    );
  }

  if (call.status === 'accepted') {
    const segment = call.limits?.pickup;
    const limitMs = segmentMs(segment);
    const anchor = pickupSlaAnchor(call.statusHistory);
    if (limitMs == null || !anchor) return null;
    const remainingMs = limitMs - (now - anchor.getTime());
    const expectedAt = new Date(anchor.getTime() + limitMs);
    return buildSlaView(
      'pickup',
      'Coleta',
      call.clientAddress || 'Origem',
      remainingMs,
      limitMs,
      expectedAt,
      isTightDistance(segment, remainingMs)
    );
  }

  if (call.status === 'ongoing' || call.status === 'partialComplete') {
    const index = activeDeliveryIndex(call);
    const segment = call.limits?.delivery?.[index];
    const limitMs = segmentMs(segment);
    const anchor = lastHistoryDate(call.statusHistory, call.status === 'partialComplete' ? 'partialComplete' : 'ongoing')
      || lastHistoryDate(call.statusHistory, 'ongoing');
    if (limitMs == null || !anchor) return null;
    const remainingMs = limitMs - (now - anchor.getTime());
    const destAddress = call.destinations?.[index]?.address;
    const deliveryStartedAt = lastHistoryDate(call.statusHistory, 'ongoing') || anchor;
    const expectedAt = new Date(
      deliveryStartedAt.getTime() + previousDestinationsMs(call, index) + limitMs
    );
    return {
      stage: 'delivery',
      stageLabel: 'Entrega',
      destinationLabel: destAddress || `Destino ${index + 1}`,
      remainingMs,
      limitMs,
      tone: slaTone(remainingMs),
      formatted: formatSlaClock(remainingMs, limitMs),
      expectedAt,
      expectedClock: formatExpectedClock(expectedAt),
      tightDistance: isTightDistance(segment, remainingMs)
    };
  }

  return null;
};

export interface SlaSegmentRow {
  label: string;
  address: string;
  plannedMinutes: number | null;
  active: boolean;
  view: SlaView | null;
}

export const getSlaTimeline = (call: SlaCallLike, now: number): SlaSegmentRow[] => {
  const active = getActiveSla(call, now);
  const pickupMinutes = call.limits?.pickup?.durationInMinutes ?? null;
  const rows: SlaSegmentRow[] = [];

  if (call.status === 'waiting' || call.status === 'forwarded' || active?.stage === 'waiting') {
    rows.push({
      label: active?.stageLabel || 'Aguardando',
      address: active?.destinationLabel || 'Sem condutor atribuído',
      plannedMinutes: WAITING_SLA_MINUTES,
      active: active?.stage === 'waiting',
      view: active?.stage === 'waiting' ? active : null
    });
  }

  rows.push({
    label: 'Coleta',
    address: call.clientAddress || 'Origem',
    plannedMinutes: pickupMinutes,
    active: active?.stage === 'pickup',
    view: active?.stage === 'pickup' ? active : null
  });

  const deliveries = call.limits?.delivery ?? [];
  const destCount = Math.max(deliveries.length, call.destinations?.length ?? 0);
  for (let i = 0; i < destCount; i += 1) {
    const planned = deliveries[i]?.durationInMinutes ?? null;
    const isActive = active?.stage === 'delivery' && activeDeliveryIndex(call) === i;
    rows.push({
      label: `Entrega ${i + 1}`,
      address: call.destinations?.[i]?.address || `Destino ${i + 1}`,
      plannedMinutes: planned,
      active: isActive,
      view: isActive ? active : null
    });
  }

  return rows;
};

export interface SlaSummary {
  waiting: { ok: number; warn: number; late: number };
  pickup: { ok: number; warn: number; late: number };
  delivery: { ok: number; warn: number; late: number };
}

export const emptySlaSummary = (): SlaSummary => ({
  waiting: { ok: 0, warn: 0, late: 0 },
  pickup: { ok: 0, warn: 0, late: 0 },
  delivery: { ok: 0, warn: 0, late: 0 }
});

export const summarizeSla = (calls: SlaCallLike[], now: number): SlaSummary => {
  const summary = emptySlaSummary();
  calls.forEach((call) => {
    const view = getActiveSla(call, now);
    if (!view) return;
    summary[view.stage][view.tone] += 1;
  });
  return summary;
};

export const slaProgressRatio = (view: SlaView): number => {
  if (!view.limitMs) return 0;
  return Math.max(0, Math.min(1, view.remainingMs / view.limitMs));
};

const mixChannel = (from: number, to: number, t: number): number => Math.round(from + (to - from) * t);

export const slaProgressColor = (ratio: number): string => {
  const elapsed = 1 - Math.max(0, Math.min(1, ratio));
  const green = { r: 46, g: 125, b: 50 };
  const yellow = { r: 249, g: 168, b: 37 };
  const red = { r: 198, g: 40, b: 40 };
  const from = elapsed < 0.5 ? green : yellow;
  const to = elapsed < 0.5 ? yellow : red;
  const t = elapsed < 0.5 ? elapsed * 2 : (elapsed - 0.5) * 2;
  return `rgb(${mixChannel(from.r, to.r, t)}, ${mixChannel(from.g, to.g, t)}, ${mixChannel(from.b, to.b, t)})`;
};

export const slaSortRank = (call: SlaCallLike, now: number): number => {
  const view = getActiveSla(call, now);
  if (!view) return Number.MAX_SAFE_INTEGER;
  if (view.tone === 'late') return view.remainingMs;
  if (view.tone === 'warn') return 1e12 + view.remainingMs;
  return 2e12 + view.remainingMs;
};

export const playSlaAlert = (): void => {
  try {
    const AudioCtx = window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 880;
    gain.gain.value = 0.08;
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.18);
  } catch {
    // ignore autoplay restrictions
  }
};
